import React, { useMemo } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

export default function DataTable({
  rows = [],
  columns = [],
  rowKey = "id",

  // selección
  selectable = true,
  selectedIds = [],
  onToggleRow,
  onToggleAll,

  // sorting (opcional)
  sortConfig,
  onSort,

  // click en fila (opcional)
  onRowClick,

  // loading
  loading,
  loadingFallback = null,

  // clases
  tableClassName = "custom-table",
  wrapperClassName = "card-table",
  rowClassName = "table-row-hover",

  // footer/paginación dentro del mismo card
  footer = null,

  // Empty state
  emptyTitle = "Aún no hay registros.",
  emptySubtitle = "Cuando agregues información, aparecerá aquí.",
  emptyIcon = null,
  emptyAction = null, // ej: botón "Nuevo"
}) {
  const visibleCols = useMemo(
    () => columns.filter((c) => c.visible !== false),
    [columns],
  );

  const allSelected =
    rows.length > 0 && selectedIds.length === rows.length && selectable;

  const renderSortIcon = (col) => {
    if (!col.sortKey || !sortConfig || !onSort) return null;
    if (sortConfig.key !== col.sortKey) return null;

    return sortConfig.direction === "asc" ? (
      <ArrowUp
        size={16}
        style={{ marginLeft: 6, color: "var(--accent, #3B82F6)" }}
      />
    ) : (
      <ArrowDown
        size={16}
        style={{ marginLeft: 6, color: "var(--accent, #3B82F6)" }}
      />
    );
  };

  const showEmpty = !loading && (!rows || rows.length === 0);

  return (
    <div className={wrapperClassName}>
      {loading ? (
        loadingFallback
      ) : showEmpty ? (
        <div
          style={{
            minHeight: 420,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          }}
        >
          <div style={{ textAlign: "center", maxWidth: 520 }}>
            <div style={{ marginBottom: 14 }}>
              {emptyIcon ?? (
                <svg width="200" height="140" viewBox="0 0 220 140" fill="none">
                  <path
                    d="M76 100c-18 0-32-13-32-30 0-16 12-29 28-30 6-18 23-30 42-30 22 0 40 16 43 37 15 2 27 14 27 30 0 17-14 31-32 31H76z"
                    fill="#EAF4FF"
                  />
                  <path
                    d="M92 108c-22 0-40-16-40-36 0-19 15-34 34-36 7-21 27-36 50-36 27 0 49 19 53 45 18 3 32 18 32 36 0 20-18 36-40 36H92z"
                    fill="#DCEEFF"
                    opacity="0.85"
                  />
                  <text
                    x="110"
                    y="128"
                    textAnchor="middle"
                    fontSize="22"
                    fill="#CFE3FF"
                    style={{ fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    N/A
                  </text>
                </svg>
              )}
            </div>

            <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 600 }}>
              {emptyTitle}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
              {emptySubtitle}
            </div>

            {emptyAction ? (
              <div
                style={{
                  marginTop: 18,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {emptyAction}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <table className={tableClassName}>
            <thead>
              <tr>
                {selectable && (
                  <th className="table-th" style={{ width: "50px" }}>
                    <input
                      type="checkbox"
                      className="checkbox-custom"
                      checked={allSelected}
                      onChange={onToggleAll}
                      disabled={rows.length === 0}
                    />
                  </th>
                )}

                {visibleCols.map((col) => (
                  <th
                    key={col.id}
                    className={col.headerClassName || "table-th"}
                    onClick={
                      col.sortKey && onSort
                        ? () => onSort(col.sortKey)
                        : undefined
                    }
                    style={{
                      cursor: col.sortKey && onSort ? "pointer" : "default",
                      ...(col.headerStyle || {}),
                    }}
                  >
                    {col.label} {renderSortIcon(col)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => {
                const id = row?.[rowKey];

                return (
                  <tr
                    key={id}
                    className={rowClassName}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {selectable && (
                      <td
                        className="table-td col-selection"
                        style={{ width: "50px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="checkbox-custom"
                            checked={selectedIds.includes(id)}
                            onChange={() => onToggleRow?.(id)}
                          />
                        </div>
                      </td>
                    )}

                    {visibleCols.map((col) => (
                      <td
                        key={col.id}
                        className={col.cellClassName || "table-td"}
                        style={col.cellStyle || undefined}
                        onClick={
                          col.stopPropagation
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {col.render ? col.render(row) : row?.[col.accessor]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {footer}
        </>
      )}
    </div>
  );
}
